import React, { createContext, useContext, useState, useCallback } from 'react'
import { ChatMessage, ChatRequest, ChatResponse } from '../types'
import { chatApi } from '../api/client'
import { useWeather } from './WeatherContext'

interface ChatContextType {
  messages: ChatMessage[]
  conversationId: string | null
  isLoading: boolean
  error: string | null
  sendMessage: (message: string) => Promise<void>
  clearConversation: () => void
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { location, fetchForecast } = useWeather()

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return

    setIsLoading(true)
    setError(null)

    // Add user message immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    }
    setMessages((prev) => [...prev, userMessage])

    try {
      const request: ChatRequest = {
        message,
        conversation_id: conversationId || undefined,
        location: location || undefined,
      }

      const response: ChatResponse = await chatApi.sendMessage(request)

      // Update conversation ID
      if (!conversationId) {
        setConversationId(response.conversation_id)
      }

      // Add assistant message
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: response.timestamp,
      }
      setMessages((prev) => [...prev, assistantMessage])

      // Update forecast if provided
      if (response.forecast_data && location) {
        fetchForecast(location)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message'
      setError(errorMessage)
      console.error('Error sending message:', err)
    } finally {
      setIsLoading(false)
    }
  }, [conversationId, location, fetchForecast])

  const clearConversation = useCallback(() => {
    setMessages([])
    setConversationId(null)
    setError(null)
  }, [])

  return (
    <ChatContext.Provider
      value={{
        messages,
        conversationId,
        isLoading,
        error,
        sendMessage,
        clearConversation,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export const useChat = () => {
  const context = useContext(ChatContext)
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
}
